import React, {
  lazy,
  Suspense,
  Fragment,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import {
  Switch,
  Redirect,
  Route,
} from 'react-router-dom';
import MainLayout from 'layouts/MainLayout';
import LoadingScreen from 'components/LoadingScreen';
import AuthGuard from 'components/Gaurds/AuthGuard';
import GuestGuard from 'components/Gaurds/GuestGuard';
import AuthService from 'services/AuthService';
import PermissionsPage from 'views/pages/Errors/permissions';
import { withPermissionsChecker } from 'wrappers';

const routesConfig = [
  {
    exact: true,
    path: '/',
    component: () => <Redirect to="/login" />,
  },
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('views/pages/Error404View')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('views/auth/LoginView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('views/auth/RegisterView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register/success',
    component: lazy(() => import('views/auth/RegisterView/VistaIntermedia')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register/:referralId',
    component: lazy(() => import('views/auth/RegisterView')),
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/resetPassword/:token',
    component: lazy(() => import('views/auth/ResetPasswordView')),
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/miinformacionpersonal" />,
      },
      {
        exact: true,
        path: '/app/account',
        component: lazy(() => import('views/pages/AccountView')),
      },
      {
        exact: true,
        path: '/app/reports/dashboard',
        component: lazy(() => import('views/reports/DashboardView')),
      },
      {
        exact: true,
        path: '/app/miinformacionpersonal',
        component: lazy(() => import('views/informacionPersonal')),
        permission: [
          'app:general:miinformacionpersonal:miinformacionpersonal:view',
        ],
      },
      {
        exact: true,
        path: '/app/perfil',
        component: lazy(() => import('views/perfil')),
        permission: [
          'app:perfil:general',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/catalogo/categoriaproductos',
        component: lazy(() => import('views/maestros/catalogo/categoriaProducto')),
        permission: [
          'app:general:configuracion:catalogo:categoriaproductos:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/catalogo/categoriaproductos',
        component: lazy(() => import('views/maestros/catalogo/categoriaProducto')),
        permission: [
          'app:general:configuracion:catalogo:categoriaproductos:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/catalogo/productos',
        component: lazy(() => import('views/maestros/catalogo/productos/List')),
        permission: [
          'app:general:configuracion:catalogo:productos:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/catalogo/productos/new',
        component: lazy(() => import('views/maestros/catalogo/productos/New')),
        permission: [
          'app:general:configuracion:catalogo:productos:new',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/catalogo/productos/edit/:id',
        component: lazy(() => import('views/maestros/catalogo/productos/Edit')),
        permission: [
          'app:general:configuracion:catalogo:productos:edit',
        ],
      },
      {
        exact: true,
        path: '/app/bonos',
        component: lazy(() => import('views/bonos')),
        permission: [
          'app:general:bonos',
        ],
      },
      {
        exact: true,
        path: '/app/administracion/detallebonos',
        component: lazy(() => import('views/bonos/liquidacionesAdmin/List')),
      },
      {
        exact: true,
        path: '/app/administracion/liquidacionbonos',
        component: lazy(() => import('views/bonos/allLiquidaciones/List')),
      },
      {
        exact: true,
        path: '/app/administracion/detallebonos/edit/:id',
        component: lazy(() => import('views/bonos/liquidacionesAdmin/Edit')),
        permission: [
          'app:administracion:liquidacionbonos:control',
        ],
      },
      {
        exact: true,
        path: '/app/bonosreferidos/referidos',
        component: lazy(() => import('views/referidos/arbol')),
      },
      {
        exact: true,
        path: '/app/miscompras',
        component: lazy(() => import('views/misCompras')),
        permission: [
          'app:general:miscompras:miscompras:view',
        ],
      },
      {
        exact: true,
        path: '/app/miscompras/view/:id',
        component: lazy(() => import('views/misCompras/tabs/misCompras/View')),
        permission: [
          'app:general:miscompras:miscompras:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/terminosycondiciones',
        component: lazy(() => import('views/maestros/general/terminosYCondiciones/List')),
        permission: [
          'app:general:configuracion:generales:terminosycondiciones:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/tiposusuario',
        component: lazy(() => import('views/maestros/general/tiposUsuario')),
        permission: [
          'app:general:configuracion:generales:tiposusuario:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/tiposusuario',
        component: lazy(() => import('views/maestros/general/tiposUsuario')),
        permission: [
          'app:general:configuracion:generales:tiposusuario:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/parentesco',
        component: lazy(() => import('views/maestros/general/parentesco')),
        permission: [
          'app:general:configuracion:generales:parentesco:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/parentesco',
        component: lazy(() => import('views/maestros/general/parentesco')),
        permission: [
          'app:general:configuracion:generales:parentesco:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/divisionPolitica/paises',
        component: lazy(() => import('views/configuracion/divisionPolitica/pais')),
        permission: [
          'app:general:configuracion:generales:divisionpolitica:paises:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/divisionPolitica/departamentos',
        component: lazy(() => import('views/configuracion/divisionPolitica/departamento')),
        permission: [
          'app:general:configuracion:generales:divisionpolitica:departamentos:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/divisionPolitica/ciudades',
        component: lazy(() => import('views/configuracion/divisionPolitica/ciudad')),
        permission: [
          'app:general:configuracion:generales:divisionpolitica:ciudades:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/terminosycondiciones/new',
        component: lazy(() => import('views/maestros/general/terminosYCondiciones/New')),
        permission: [
          'app:general:configuracion:generales:terminosycondiciones:new',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/terminosycondiciones/edit/:id',
        component: lazy(() => import('views/maestros/general/terminosYCondiciones/Edit')),
        permission: [
          'app:general:configuracion:generales:terminosycondiciones:edit',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/usuarios/roles',
        component: lazy(() => import('views/configuracion/usuarios/roles/list')),
      },
      {
        exact: true,
        path: '/app/configuracion/usuarios/roles/new',
        component: lazy(() => import('views/configuracion/usuarios/roles/new')),
      },
      {
        exact: true,
        path: '/app/configuracion/usuarios/roles/edit/:id',
        component: lazy(() => import('views/configuracion/usuarios/roles/edit')),
      },
      {
        exact: true,
        path: '/app/configuracion/usuarios/usuario/',
        component: lazy(() => import('views/configuracion/usuarios/usuario/list')),
      },
      {
        exact: true,
        path: '/app/configuracion/usuarios/usuario/new',
        component: lazy(() => import('views/configuracion/usuarios/usuario/new')),
      },
      {
        exact: true,
        path: '/app/configuracion/usuarios/usuario/edit/:id',
        component: lazy(() => import('views/configuracion/usuarios/usuario/edit')),
      },
      {
        exact: true,
        path: '/app/centrodeayuda/sugerencias',
        component: lazy(() => import('views/procesos/centroAyuda/sugerencias/List')),
        permission: [
          'app:general:centrodeayuda:sugerencias:view',
        ],
      },
      {
        exact: true,
        path: '/app/centrodeayuda/agendardemostracion',
        component: lazy(() => import('views/procesos/centroAyuda/agendarDemostraciones/List')),
        permission: [
          'app:centrodeayuda:agendarDemostraciones',
        ],
      },
      {
        exact: true,
        path: '/app/centrodeayuda/enviarpreguntas',
        component: lazy(() => import('views/procesos/centroAyuda/enviarPreguntas/List')),
        permission: [
          'app:general:centrodeayuda:enviarpreguntas:view',
        ],
      },
      {
        exact: true,
        path: '/app/centrodeayuda/preguntasfrecuentes',
        component: lazy(() => import('views/procesos/centroAyuda/preguntasFrecuentes/List')),
        permission: ['app:general:centrodeayuda:preguntasfrecuentes:view'],
      },
      {
        exact: true,
        path: '/app/centrodeayuda/preguntasfrecuentes/edit/:id',
        component: lazy(() => import('views/procesos/centroAyuda/preguntasFrecuentes/Edit')),
        permission: ['app:general:centrodeayuda:preguntasfrecuentes:edit'],
      },
      {
        exact: true,
        path: '/app/configuracion/centrodeayuda/preguntasfrecuentes',
        component: lazy(() => import('views/configuracion/centroAyuda/preguntasFrecuentes/List')),
        permission: ['app:general:configuracion:centrodeayuda:preguntasfrecuentes:view'],
      },
      {
        exact: true,
        path: '/app/configuracion/centrodeayuda/preguntasfrecuentes/new',
        component: lazy(() => import('views/configuracion/centroAyuda/preguntasFrecuentes/New')),
        permission: ['app:general:configuracion:centrodeayuda:preguntasfrecuentes:new'],
      },
      {
        exact: true,
        path: '/app/configuracion/centrodeayuda/preguntasfrecuentes/edit/:id',
        component: lazy(() => import('views/configuracion/centroAyuda/preguntasFrecuentes/Edit')),
        permission: ['app:general:configuracion:centrodeayuda:preguntasfrecuentes:edit'],
      },
      {
        exact: true,
        path: '/app/configuracion/centrodeayuda/ayudasgraficas',
        component: lazy(() => import('views/maestros/centroAyuda/ayudasGraficas/List')),
        permission: [
          'app:general:configuracion:centrodeayuda:ayudasgraficas:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/centrodeayuda/ayudasgraficas/new',
        component: lazy(() => import('views/maestros/centroAyuda/ayudasGraficas/New')),
        permission: [
          'app:general:configuracion:centrodeayuda:ayudasgraficas:new',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/centrodeayuda/ayudasgraficas/edit/:id',
        component: lazy(() => import('views/maestros/centroAyuda/ayudasGraficas/Edit')),
        permission: [
          'app:general:configuracion:centrodeayuda:ayudasgraficas:edit',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/metodosdepago',
        component: lazy(() => import('views/maestros/general/metodosPago')),
        permission: [
          'app:general:configuracion:generales:metodosdepago:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/metodosdepago',
        component: lazy(() => import('views/maestros/general/metodosPago')),
        permission: [
          'app:general:configuracion:generales:metodosdepago:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/bancos',
        component: lazy(() => import('views/maestros/general/bancos')),
        permission: [
          'app:general:configuracion:generales:bancos:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/bancos',
        component: lazy(() => import('views/maestros/general/bancos')),
        permission: [
          'app:general:configuracion:generales:bancos:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/monedas',
        component: lazy(() => import('views/maestros/general/monedas')),
        permission: [
          'app:general:configuracion:generales:monedas:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/generales/monedas',
        component: lazy(() => import('views/maestros/general/monedas')),
        permission: [
          'app:general:configuracion:generales:monedas:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miscompras/tipossuscripciones',
        component: lazy(() => import('views/maestros/misCompras/tiposSuscripcion/List')),
      },
      {
        exact: true,
        path: '/app/configuracion/miscompras/tipossuscripciones/new',
        component: lazy(() => import('views/maestros/misCompras/tiposSuscripcion/New')),
      },
      {
        exact: true,
        path: '/app/configuracion/miscompras/tipossuscripciones/edit/:id',
        component: lazy(() => import('views/maestros/misCompras/tiposSuscripcion/Edit')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/servicios',
        component: lazy(() => import('views/maestros/miPerfil/servicios')),
        permission: [
          'app:configuracion:miperfil:servicios',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/clasificacionusuarios',
        component: lazy(() => import('views/maestros/miPerfil/clasificacionUsuarios')),
        permission: [
          'app:configuracion:miperfil:servicios',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/servicios',
        component: lazy(() => import('views/maestros/miPerfil/servicios')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/identidadessexuales',
        component: lazy(() => import('views/maestros/miPerfil/identidadSexual')),
        permission: [
          'app:configuracion:miperfil:identidadessexuales',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/identidadessexuales',
        component: lazy(() => import('views/maestros/miPerfil/identidadSexual')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/orientacionessexuales',
        component: lazy(() => import('views/maestros/miPerfil/orientacionSexual')),
        permission: [
          'app:configuracion:miperfil:orientacionessexuales',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/orientacionessexuales',
        component: lazy(() => import('views/maestros/miPerfil/orientacionSexual')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/tiposdeoral',
        component: lazy(() => import('views/maestros/miPerfil/tiposOrales')),
        permission: [
          'app:configuracion:miperfil:tiposdeoral',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/tiposdeoral',
        component: lazy(() => import('views/maestros/miPerfil/tiposOrales')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/tiposdefantasiasyfetiches',
        component: lazy(() => import('views/maestros/miPerfil/tiposFantasiasFetiches')),
        permission: [
          'app:configuracion:miperfil:tiposdefantasiasyfetiches',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/tiposdefantasiasyfetiches',
        component: lazy(() => import('views/maestros/miPerfil/tiposFantasiasFetiches')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/tiposdemasajes',
        component: lazy(() => import('views/maestros/miPerfil/tiposMasajes')),
        permission: [
          'app:configuracion:miperfil:tiposdemasajes',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/tiposdemasajes',
        component: lazy(() => import('views/maestros/miPerfil/tiposMasajes')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/serviciosvirtuales',
        component: lazy(() => import('views/maestros/miPerfil/serviciosVirtuales')),
        permission: [
          'app:configuracion:miperfil:serviciosvirtuales',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/serviciosvirtuales',
        component: lazy(() => import('views/maestros/miPerfil/serviciosVirtuales')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/ojos',
        component: lazy(() => import('views/maestros/miPerfil/ojos')),
        permission: [
          'app:configuracion:miperfil:ojos',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/ojos',
        component: lazy(() => import('views/maestros/miPerfil/ojos')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/cabello',
        component: lazy(() => import('views/maestros/miPerfil/cabellos')),
        permission: [
          'app:configuracion:miperfil:cabello',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/cabello',
        component: lazy(() => import('views/maestros/miPerfil/cabellos')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/cuerpo',
        component: lazy(() => import('views/maestros/miPerfil/cuerpos')),
        permission: [
          'app:configuracion:miperfil:cuerpo',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/cuerpo',
        component: lazy(() => import('views/maestros/miPerfil/cuerpos')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/piel',
        component: lazy(() => import('views/maestros/miPerfil/pieles')),
        permission: [
          'app:configuracion:miperfil:piel',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/piel',
        component: lazy(() => import('views/maestros/miPerfil/pieles')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/busto',
        component: lazy(() => import('views/maestros/miPerfil/bustos')),
        permission: [
          'app:configuracion:miperfil:busto',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/busto',
        component: lazy(() => import('views/maestros/miPerfil/bustos')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/cola',
        component: lazy(() => import('views/maestros/miPerfil/colas')),
        permission: [
          'app:configuracion:miperfil:cola',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/cola',
        component: lazy(() => import('views/maestros/miPerfil/colas')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/polla',
        component: lazy(() => import('views/maestros/miPerfil/pollas')),
        permission: [
          'app:configuracion:miperfil:polla',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/polla',
        component: lazy(() => import('views/maestros/miPerfil/pollas')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/idioma',
        component: lazy(() => import('views/maestros/miPerfil/idiomas')),
        permission: [
          'app:configuracion:miperfil:idioma',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/idioma',
        component: lazy(() => import('views/maestros/miPerfil/idiomas')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/depilacion',
        component: lazy(() => import('views/maestros/miPerfil/depilaciones')),
        permission: [
          'app:configuracion:miperfil:depilacion',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/depilacion',
        component: lazy(() => import('views/maestros/miPerfil/depilaciones')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/etnia',
        component: lazy(() => import('views/maestros/miPerfil/etnias')),
        permission: [
          'app:configuracion:miperfil:etnia',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/etnia',
        component: lazy(() => import('views/maestros/miPerfil/etnias')),
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/criptomoneda',
        component: lazy(() => import('views/maestros/miPerfil/criptomonedas')),
        permission: [
          'app:general:configuracion:miperfil:criptomoneda:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/criptomoneda',
        component: lazy(() => import('views/maestros/miPerfil/criptomonedas')),
        permission: [
          'app:general:configuracion:miperfil:criptomoneda:view',
        ],
      },
      /*
      {
        exact: true,
        path: '/app/configuracion/miperfil/red',
        component: lazy(() => import('views/maestros/miPerfil/redes')),
        permission: [
          'app:configuracion:miperfil:red',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/miperfil/red',
        component: lazy(() => import('views/maestros/miPerfil/redes')),
      },
      */
      {
        exact: true,
        path: '/app/configuracion/seguridad/usuarios',
        component: lazy(() => import('views/seguridad/usuarios/List')),
        permission: [
          'app:general:configuracion:seguridad:usuarios:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/usuarios/new',
        component: lazy(() => import('views/seguridad/usuarios/New')),
        permission: [
          'app:general:configuracion:seguridad:usuarios:new',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/usuarios/edit/:id',
        component: lazy(() => import('views/seguridad/usuarios/Edit')),
        permission: [
          'app:general:configuracion:seguridad:usuarios:edit',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/cambiodecontrasena',
        component: lazy(() => import('views/seguridad/perfil')),
        permission: [
          'app:general:configuracion:seguridad:cambiodecontraseña:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/roles',
        component: lazy(() => import('views/seguridad/roles/List')),
        permission: [
         'app:general:configuracion:seguridad:roles:view',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/roles/new',
        component: lazy(() => import('views/seguridad/roles/New')),
        permission: [
         'app:general:configuracion:seguridad:roles:new',
        ],
      },
      {
        exact: true,
        path: '/app/configuracion/seguridad/roles/edit/:id',
        component: lazy(() => import('views/seguridad/roles/Edit')),
        permission: [
         'app:general:configuracion:seguridad:roles:edit',
        ],
      },
      /*
      {
        exact: true,
        path: '/app/configuracion/miscompras/tipossuscripcion',
        component: lazy(() => import('views/maestros/misCompras/tiposSuscripcion')),
      },
      {
        exact: true,
        path: '/app/configuracion/miscompras/tipossuscripcion',
        component: lazy(() => import('views/maestros/misCompras/tiposSuscripcion')),
      },
      */
      {
        exact: true,
        path: '/app/consultas/usuariosregistrados',
        component: lazy(() => import('views/consultas/estadisticas/userRegister/List')),
        permission: [
          'app:general:consultas:usuariosregistrados:view',
        ],
      },
      {
        exact: true,
        path: '/app/consultas/suscripciones',
        component: lazy(() => import('views/consultas/estadisticas/suscripciones/List')),
        permission: [
          'app:consultas:usuariosregistrados',
        ],
      },
      {
        exact: true,
        path: '/app/consultas/historicocambiodeestados',
        component: lazy(() => import('views/consultas/estadisticas/cambiosEstados')),
        permission: [
          'app:general:consultas:historicocambioestados:view',
        ],
      },
      {
        exact: true,
        path: '/app/consultas/usuariosregistrados/view/:id',
        component: lazy(() => import('views/consultas/estadisticas/userRegister/View')),
        // falta por permisos
      },
      {
        exact: true,
        path: '/app/consultas/usuariosregistrados/edit/:id',
        component: lazy(() => import('views/consultas/estadisticas/userRegister/Edit')),
        // falta por permisos
      },
      {
        exact: true,
        path: '/app/administracion/controlinformacionpersonal',
        component: lazy(() => import('views/procesos/estadisticas/informacionPersonal')),
        permission: [
          'app:general:administracion:controlinformacionpersonal:view',
        ],
      },
      {
        exact: true,
        path: '/app/administracion/compras/',
        component: lazy(() => import('views/administracion/misCompras/compras')),
        permission: [
          'app:general:administracion:compras:view',
        ],
      },
      {
        exact: true,
        path: '/app/administracion/compraproducto',
        component: lazy(() => import('views/administracion/compraProducto')),
        permission: [
          'app:general:administracion:compraproducto:view',
        ],
      },
      {
        exact: true,
        path: '/app/administracion/controlinformacionpersonal/view/:id',
        component: lazy(() => import('views/procesos/estadisticas/informacionPersonal/indexInfo')),
        permission: [
          'app:general:administracion:controlinformacionpersonal:view',
        ],
      },
      {
        exact: true,
        path: '/app/administracion/publicaciones/control',
        component: lazy(() => import('views/procesos/estadisticas/publicaciones')),
        permission: [
          'app:administracion:publicaciones:control',
        ],
      },
      {
        exact: true,
        path: '/app/administracion/publicaciones/control/view/:id',
        component: lazy(() => import('views/procesos/estadisticas/publicaciones/tabs')),
      },
      {
        exact: true,
        path: '/app/administracion/perfil/control',
        component: lazy(() => import('views/procesos/estadisticas/perfil')),
        permission: [
          'app:administracion:perfil:control',
        ],
      },
      {
        exact: true,
        path: '/app/administracion/perfil/control/view/:id',
        component: lazy(() => import('views/procesos/estadisticas/perfil/indexInformacion')),
      },
      {
        exact: true,
        path: '/app/administracion/suscripciones/control',
        component: lazy(() => import('views/procesos/estadisticas/suscripciones')),
        permission: [
          'app:administracion:suscripciones:control',
        ],
      },
      {
        component: () => <Redirect to="/404" />,
      },
    ],
  },
];

const MapRoute = ({ Component, route }, key) => (
  <Route
    key={String(key)}
    path={route.path}
    exact={route.exact}
    render={() => {
      if (AuthService.isTokenValid()) {
        return <Component />;
      }
      return <Redirect to="/login" />;
    }}
  />
);

MapRoute.propTypes = {
  Component: PropTypes.node,
  route: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape(),
  ]),
};

const renderRoutes = (checkAccess, routes) => (routes ? (
  <Switch>
    {routes.map((route, i) => {
      const Guard = route.guard || Fragment;
      const Layout = route.layout || Fragment;
      const Component = route.component;
      const Permission = route.permission || [];
      if (checkAccess && Permission) {
        if (!checkAccess(Permission)) {
          return MapRoute({ Component: PermissionsPage, route }, i);
        }
      }
      return (
        <Route
          key={`${String(i)}`}
          path={route.path}
          exact={route.exact}
          render={(props) => (
            <Guard>
              <Layout>
                {route.routes
                  ? renderRoutes(checkAccess, route.routes)
                  : <Component {...props} />}
              </Layout>
            </Guard>
          )}
        />
      );
    })}
  </Switch>
) : null);

const Routes = (props) => {
  const { checkAccess } = props;
  return (
    <Suspense fallback={<LoadingScreen />}>
      {renderRoutes(checkAccess, routesConfig)}
    </Suspense>
  );
};

Routes.propTypes = {
  checkAccess: PropTypes.func,
};

export default withPermissionsChecker(memo(Routes));
